// src/Components/PricingPlans.jsx
import React, { useState } from "react";
import {
  Container,
  Grid,
  Switch,
  Typography,
  Box,
  Button,
  Card,
  CardContent,
  List,
  ListItem,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { auth } from "../utils/firebase"; // Ensure you have Firebase configured

const baseurl = process.env.REACT_APP_API_URL; // Replace this with your base URL

/**
 * PricingPlans
 *
 * - LEFT = Monthly (isMonthly = true)
 * - RIGHT = Annually (isMonthly = false)
 *
 * Switch.checked is set to {!isMonthly} so that:
 * - isMonthly === true => Switch unchecked (thumb left)
 * - isMonthly === false => Switch checked (thumb right)
 *
 * Note: Professional uses the old Basic's Stripe price IDs (no backend conflicts).
 * Replace placeholder price IDs with your real Stripe IDs where noted.
 */

const PricingPlans = () => {
    // false = annually plan active (default)
  const [isMonthly, setIsMonthly] = useState(false);
  const navigate = useNavigate();

  // --- Pricing constants ---
  // New Basic plan prices
  const NEW_BASIC_MONTHLY = 59;
  const NEW_BASIC_ANNUAL_MONTHLY = 49;
  const NEW_BASIC_ANNUAL_TOTAL = NEW_BASIC_ANNUAL_MONTHLY * 12; // 49 * 12 = 588

  // Professional plan (previously Basic)
  const PROFESSIONAL_MONTHLY = 198;
  const PROFESSIONAL_ANNUAL_MONTHLY = 180;
  const PROFESSIONAL_ANNUAL_TOTAL = PROFESSIONAL_ANNUAL_MONTHLY * 12; // 180 * 12 = 2160

  // --- Plans array (left -> Basic, middle -> Professional, right -> Agency) ---
  const plans = [
    
    {
      // New Basic (left column)
      //title: "Professional",
      title: "Basic",
      // badge: "Most popular", // Temporarily moved to the Agency plan.
      monthlyDisplay: `$${PROFESSIONAL_MONTHLY}/month`,
      annualDisplay: `$${PROFESSIONAL_ANNUAL_MONTHLY}/month`,
      monthlyPrice: `$${PROFESSIONAL_MONTHLY}`,
      annualPrice: `$${PROFESSIONAL_ANNUAL_MONTHLY}`,
      monthlyNote: "billed monthly, cancel anytime",
      annualNote: "billed annually ($2,160/yr)",

      items: [
        "AI Grant Proposal Writer",
        "AI RFP Response Writer",
        "Unlimited Proposals",
        "Unlimited Proposal Templates",
        "15 AI Interactions / month",
        "10 Upload Documents / month",
        "Export Grants To PDF",
      ],
      // <-- THESE ARE THE ORIGINAL BASIC STRIPE IDs (kept to avoid backend conflicts)
      monthlyPriceId: "price_1QG1EQL8ltvzfICgHwtVtU2Y",
      // yearlyPriceId: "price_1U6dVvL8ltvzfICgbuhWDsLk" //test yearly price id for basic plan
      yearlyPriceId: "price_1TLDXFL8ltvzfICgAo9gQzxB"
    },
    /*
    {
      // New Professional (middle column)
      //title: "Basic",
      title: "Professional(TBD Name)",
      monthlyDisplay: `$${NEW_BASIC_MONTHLY}/month`,
      annualDisplay: `$${NEW_BASIC_ANNUAL_MONTHLY}/month `,

      items: [
        "AI Grant Proposal Writer (basic)",
        "AI RFP Response Writer",
        "5 AI Interactions / month",
        "3 Upload Documents / month",
      ],
      // <-- REPLACE THESE placeholder IDs with your real Stripe price IDs for Basic
      monthlyPriceId: "price_basic_monthly_placeholder",
      yearlyPriceId: "price_basic_yearly_placeholder",
    },
    */
    {
      // Agency (right column) — no visible pricing on card
      title: "Agency",
      badge: "Most popular",
      monthlyDisplay: "",
      annualDisplay: "",
      monthlyPrice: "Custom",
      annualPrice: "Custom",
      monthlyNote: "Custom package pricing available",
      annualNote: "Custom package pricing available",
      items: [
        "Live Support",
        "Unlimited Clients",
        "Grant Search Database",
        "Unlimited Industry Report",
        "AI Grant Proposal Writer",
        "Unlimited Document Uploads",
        "Export Grants To PDF & DOCX",
      ],
      // Keep or replace with your Agency Stripe IDs if you use them server-side
      monthlyPriceId: "price_id_monthly_agency",
      yearlyPriceId: "price_id_yearly_agency",
    },
  ];

  // Toggle handler
  const handleSwitchChange = () => {
    setIsMonthly((prev) => !prev);
  };

  // Plan selection: preserves unauthenticated redirect and Stripe flow
  const handlePlanSelection = async (plan) => {
    const priceId = isMonthly ? plan.monthlyPriceId : plan.yearlyPriceId;
    const affiliateId = localStorage.getItem("affiliate_id");

    // If user not authenticated, redirect to signup with params
    if (!auth.currentUser) {
      const params = new URLSearchParams({
        plan: plan.title,
        price_id: priceId,
      });
      if (affiliateId) params.append("affiliate_id", affiliateId);
      window.location.assign(`/signup?${params.toString()}`);
      return;
    }

    try {
      const user = auth.currentUser;
      const body = {
        user_id: user.uid,
        price_id: priceId,
      };
      if (affiliateId) body.affiliate_id = affiliateId;

      const response = await fetch(`${baseurl}/create_payment_session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const txt = await response.text().catch(() => null);
        console.error("Payment session error:", txt || response.status);
        return;
      }

      const data = await response.json();
      if (data.url) {
        window.location.assign(data.url);
      } else {
        console.error("Error: Payment URL not found in response");
      }
    } catch (error) {
      console.error("Error creating payment session:", error);
    }
  };

  return (
    <Container>
      <Box textAlign="center" mb={4} mt={2}>
        <Typography sx={{ color: "#fc40ffff" }} variant="h4">
          Choose A Plan
        </Typography>

        {/* Commented out the switch between Monthly and Annually
        <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
          <Typography color="white" variant="h6" sx={{ mr: 1 }}>
            Monthly
          </Typography>

          <Switch
            checked={!isMonthly}
            onChange={handleSwitchChange}
            inputProps={{ "aria-label": "toggle pricing monthly/annual" }}
            sx={{
              "& .MuiSwitch-switchBase": {
                "&.Mui-checked": {
                  color: "#AE00FF",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#743dff",
                  },
                },
              },
              "& .MuiSwitch-track": {
                backgroundColor: "#743dff",
              },
            }}
          />

          <Typography color="white" variant="h6" sx={{ ml: 1 }}>
            Annually
          </Typography>
        </Box>
        */}

        <Box display="flex" justifyContent="center" alignItems="center" mb={4}>
          <Typography color="white" variant="h6">
            Annually
          </Typography>
        </Box>
      </Box>

      <Grid container spacing={4} justifyContent="center">
        {plans.map((plan, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={4}
            key={index}
            display="flex"
            justifyContent="center"
          >
            <Card
              className={`pricing-plan-card ${plan.title === "Basic" ? "pricing-plan-basic" : "pricing-plan-agency"}`}
              sx={{
                height: "100%",
                width: "350px",
                padding: 2,
                display: "flex",
                flexDirection: "column",
                border: "7px solid #AE00FF",
                borderRadius: "11px",
              }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography
                  variant="h4"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gutterBottom
                  sx={{
                    fontWeight: "bold",
                    color: "#00068e",
                    fontFamily: "inherit",
                  }}
                >
                  {plan.title}
                </Typography>

                {plan.badge && <span className="pricing-card-badge">{plan.badge}</span>}

                <Typography
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  gutterBottom
                >
                  {plan.tag}
                </Typography>

                <div className="pricing-card-price"><strong>{isMonthly ? plan.monthlyPrice : plan.annualPrice}</strong>{plan.title !== "Agency" && <span>/month</span>}</div>
                <div className="pricing-card-note">{isMonthly ? plan.monthlyNote : plan.annualNote}</div>

                <Box textAlign="center" pb={3}>
                  {plan.title === "Agency" ? (
                    <Button
                      component="a"
                      href="https://calendly.com/dream-big-/90min"
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: "#3c88f1",
                        color: "#fff",
                        width: "100%",
                        height: "55px",
                        textDecoration: "none",
                      }}
                    >
                      Book a Demo
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="large"
                      sx={{
                        backgroundColor: "#3c88f1",
                        color: "#fff",
                        width: "100%",
                        height: "55px",
                      }}
                      onClick={() => handlePlanSelection(plan)}
                    >
                      Choose Plan
                    </Button>
                  )}
                </Box>

                <List>
                  {plan.items.map((item, idx) => (
                    <ListItem key={idx} sx={{ color: "#7e00ff" }}>
                      {item}
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default PricingPlans;
